import React, { useEffect } from 'react';

/**
 * AdSenseBanner — Small non-intrusive Google AdSense ad unit
 *
 * Usage: <AdSenseBanner slot="1234567890" />
 *
 * Slot resolution order:
 *  1. `slot` prop (explicit)
 *  2. REACT_APP_ADSENSE_REWARDED_SLOT env var (set in Azure ASWA app settings
 *     or .env file — use this for the desktop rewarded embed unit)
 *  3. "0000000000" — placeholder, no real ad will serve
 *
 * IMPORTANT: Before going live, replace the publisher ID in:
 *  - public/index.html (<script> tag with ca-pub-XXX)
 *  - This file's `data-ad-client` attribute
 *
 * AdSense approval requirements:
 *  - Site must be live (not localhost)
 *  - Unique, high-quality content
 *  - Compliance with AdSense program policies
 *  - Usually takes 1-14 days for approval
 */

// Env-var driven slot ID for the desktop rewarded embed unit.
// Set REACT_APP_ADSENSE_REWARDED_SLOT in Azure Static Web Apps application
// settings (or .env.production) once the AdSense console slot is created.
const REWARDED_DESKTOP_SLOT_ID =
  process.env.REACT_APP_ADSENSE_REWARDED_SLOT || '0000000000';

const AdSenseBanner = ({ slot, format = 'auto', responsive = true, style }) => {
  // If no explicit slot is provided, fall back to the env-var slot.
  slot = slot || REWARDED_DESKTOP_SLOT_ID;
  useEffect(() => {
    try {
      // Push unconditionally: adsbygoogle.js drains the queued array once it
      // finishes loading, so this must not be gated on the script being ready.
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      // Silent fail — AdSense script may not be loaded yet, or blocked by ad blocker
      console.debug('AdSense load skipped:', e.message);
    }
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: 90,
        padding: '12px 0',
        ...style,
      }}
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block', width: '100%', maxWidth: 728, height: 90 }}
        data-ad-client="ca-pub-0726149070503281"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? 'true' : 'false'}
      />
    </div>
  );
};

export default AdSenseBanner;
